/**
 * Clinical AI Agent - Workflows Tab (Transfers, Discharges, Condition Changes)
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowRightLeft, LogOut, TrendingUp, Loader2, CheckCircle, AlertTriangle
} from "lucide-react";
import { 
  TRANSFER_TYPES, DISCHARGE_TYPES, CONDITION_CHANGE_TYPES, getSeverityClass 
} from "./constants";

export function TransferForm({
  transferForm,
  setTransferForm,
  loading,
  isDark,
  onSubmit
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs">Transfer Type</Label>
        <Select
          value={transferForm.transfer_type}
          onValueChange={(v) => setTransferForm({ ...transferForm, transfer_type: v })}
        >
          <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRANSFER_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs">From Location</Label>
          <Input
            placeholder="Current location"
            value={transferForm.from_location}
            onChange={(e) => setTransferForm({ ...transferForm, from_location: e.target.value })}
            className={isDark ? "bg-slate-700 border-slate-600" : ""}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">To Location</Label>
          <Input
            placeholder="New location"
            value={transferForm.to_location}
            onChange={(e) => setTransferForm({ ...transferForm, to_location: e.target.value })}
            className={isDark ? "bg-slate-700 border-slate-600" : ""}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Reason for Transfer</Label>
        <Textarea
          placeholder="Clinical reason for transfer..."
          value={transferForm.reason}
          onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Clinical Notes</Label>
        <Textarea
          placeholder="Additional notes..."
          value={transferForm.clinical_notes}
          onChange={(e) => setTransferForm({ ...transferForm, clinical_notes: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={2}
        />
      </div>

      <Button
        onClick={onSubmit}
        disabled={loading || !transferForm.from_location || !transferForm.to_location}
        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
        data-testid="initiate-transfer-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRightLeft className="h-4 w-4 mr-2" />}
        Initiate Transfer Workflow
      </Button>
    </div>
  );
}

export function DischargeForm({
  dischargeForm,
  setDischargeForm,
  loading,
  isDark,
  onSubmit
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs">Discharge Type</Label>
        <Select
          value={dischargeForm.discharge_type}
          onValueChange={(v) => setDischargeForm({ ...dischargeForm, discharge_type: v })}
        >
          <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DISCHARGE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Reason for Discharge</Label>
        <Textarea
          placeholder="Reason for discharge..."
          value={dischargeForm.reason}
          onChange={(e) => setDischargeForm({ ...dischargeForm, reason: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Clinical Notes</Label>
        <Textarea
          placeholder="Final clinical summary..."
          value={dischargeForm.clinical_notes}
          onChange={(e) => setDischargeForm({ ...dischargeForm, clinical_notes: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={2}
        />
      </div>

      <Button
        onClick={onSubmit}
        disabled={loading || !dischargeForm.reason}
        className="w-full bg-gradient-to-r from-red-600 to-orange-600"
        data-testid="initiate-discharge-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
        Initiate Discharge Workflow
      </Button>
    </div>
  );
}

export function ConditionChangeForm({
  conditionForm,
  setConditionForm,
  loading,
  isDark,
  onSubmit
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs">Change Type</Label>
        <Select
          value={conditionForm.change_type}
          onValueChange={(v) => setConditionForm({ ...conditionForm, change_type: v })}
        >
          <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONDITION_CHANGE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Severity</Label>
        <Select
          value={conditionForm.severity}
          onValueChange={(v) => setConditionForm({ ...conditionForm, severity: v })}
        >
          <SelectTrigger className={isDark ? "bg-slate-700 border-slate-600" : ""}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="moderate">Moderate</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Description</Label>
        <Textarea
          placeholder="Describe the condition change..."
          value={conditionForm.description}
          onChange={(e) => setConditionForm({ ...conditionForm, description: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Clinical Notes</Label>
        <Textarea
          placeholder="Additional clinical context..."
          value={conditionForm.clinical_notes}
          onChange={(e) => setConditionForm({ ...conditionForm, clinical_notes: e.target.value })}
          className={isDark ? "bg-slate-700 border-slate-600" : ""}
          rows={2}
        />
      </div>

      <Button
        onClick={onSubmit}
        disabled={loading || !conditionForm.description}
        className="w-full bg-gradient-to-r from-yellow-600 to-orange-600"
        data-testid="trigger-condition-change-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
        Trigger Condition Change Workflow
      </Button>
    </div>
  );
}

export function WorkflowResultDisplay({ result, isDark }) {
  if (!result) return null;

  const { type, data } = result;
  
  const isWorkflowType = ["transfer", "discharge", "condition_change"].includes(type);
  if (!isWorkflowType) return null;

  return (
    <Card className={`mt-4 ${isDark ? "bg-slate-800/80 border-blue-500/30" : "bg-blue-50 border-blue-200"}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-400" />
          {type === "transfer" ? "Transfer" : type === "discharge" ? "Discharge" : "Condition Change"} Workflow Initiated
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-64">
          {data.status && (
            <Badge className="mb-3 bg-blue-500/20 text-blue-400">
              Status: {data.status}
            </Badge>
          )}

          {/* Transfer-specific */}
          {type === "transfer" && (
            <>
              {data.transfer_checklist && (
                <div className="mb-3">
                  <p className="text-xs font-semibold mb-1">Checklist:</p>
                  <ul className="text-xs space-y-1">
                    {data.transfer_checklist.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        {item.status === "complete" ? (
                          <CheckCircle className="h-3 w-3 text-green-400" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 text-yellow-400" />
                        )}
                        {item.item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.communication_log && (
                <div className="mb-3">
                  <p className="text-xs font-semibold mb-1">Communication Log:</p>
                  <ul className="text-xs space-y-1">
                    {data.communication_log.map((item, i) => (
                      <li key={i}>{item.contact}: {item.status}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Discharge-specific */}
          {type === "discharge" && (
            <>
              {data.documentation_checklist && (
                <div className="mb-3">
                  <p className="text-xs font-semibold mb-1">Documentation Checklist:</p>
                  <ul className="text-xs space-y-1">
                    {data.documentation_checklist.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        {item.status === "complete" ? (
                          <CheckCircle className="h-3 w-3 text-green-400" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 text-yellow-400" />
                        )}
                        {item.item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.bereavement_plan && (
                <div className="mb-3 p-2 rounded bg-purple-500/10 border border-purple-500/20">
                  <p className="text-xs font-semibold mb-1 text-purple-400">Bereavement Plan:</p>
                  <p className="text-xs">Initial contact due: {new Date(data.bereavement_plan.initial_contact_due).toLocaleDateString()}</p>
                </div>
              )}
            </>
          )}

          {/* Condition Change-specific */}
          {type === "condition_change" && (
            <>
              {data.tasks_created && (
                <div className="mb-3">
                  <p className="text-xs font-semibold mb-1">Tasks Created:</p>
                  <div className="flex flex-wrap gap-1">
                    {data.tasks_created.map((task, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{task}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {data.immediate_actions && data.immediate_actions.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold mb-1 text-orange-400">Immediate Actions:</p>
                  <ul className="text-xs space-y-1">
                    {data.immediate_actions.map((action, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="text-orange-400">→</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.urgency && (
                <Badge className={getSeverityClass(data.urgency === "immediate" ? "critical" : data.urgency === "24_hours" ? "high" : "moderate")}>
                  Urgency: {data.urgency}
                </Badge>
              )}
            </>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
